/**
 * Local-first mutations for video WATCH PROGRESS (F2-B).
 *
 * Write to the local PowerSync DB (Drizzle); the sync queue sends them to the
 * server (`db/sync.rs::video_watch_progress`), which FORCES `user_id` to the actor
 * and keeps `watched_pct` MONOTONIC. The id is DETERMINISTIC `${userId}:${videoId}`
 * → a single record per (user, video), resumable offline.
 *
 * Monotonicity is also enforced client-side (don't lower the pct locally) so
 * the "resume / completed" UI stays coherent even before syncing.
 */

import { eq, type PowerSyncDrizzleDB, video_watch_progress } from "@space/powersync";
import { nowISO } from "../utils/date-helpers";

/** Deterministic id for a watch-progress row. */
export function watchProgressId(userId: string, videoId: string): string {
  return `${userId}:${videoId}`;
}

/** Clamps a percentage to [0,1]. */
export function clampPct(pct: number): number {
  if (Number.isNaN(pct)) {
    return 0;
  }
  return Math.min(1, Math.max(0, pct));
}

export interface WatchProgressUpdate {
  /** Current cursor position (seconds). Follows the client (can decrease). */
  positionSeconds: number;
  /** Percentage watched [0,1]. Applied MONOTONICALLY (max with the previous value). */
  watchedPct: number;
  /** Does the client consider the video completed (crossed minWatchPct)? */
  completed?: boolean;
}

/** Row (subset) read from the local DB for the monotonic calculation. */
interface ExistingRow {
  watched_pct: number | null;
  completed_at: string | null;
}

/**
 * Local-first upsert of watch progress. Deterministic and idempotent:
 *  - id = `${userId}:${videoId}`.
 *  - `watched_pct` never decreases (max with the previous local value).
 *  - `completed_at` is stamped once and kept.
 *
 * ⚠️ Class 2 (POWERSYNC_VERIFIED_WRITE.md §3): the SELECT that reads `prev` lives
 * inside the window. The id is DETERMINISTIC, so a false "doesn't exist" doesn't
 * duplicate a distinct row — but it CAN lose monotonicity: if the
 * real `prev.watched_pct` was higher and the SELECT sees it as empty, this INSERT
 * writes `nextPct` without comparing against the real max, and once the
 * original row syncs it may end up behind (or collide with the PK). It isn't
 * shielded with `waitForRowPresent` because that same deterministic id is the one
 * for the FIRST time anyone plays a video — waiting out the full backoff there
 * would penalize every new playback start. Documented and characterized,
 * not shielded.
 */
export async function upsertWatchProgress(
  db: PowerSyncDrizzleDB,
  userId: string,
  videoId: string,
  update: WatchProgressUpdate,
): Promise<string> {
  const id = watchProgressId(userId, videoId);
  const now = nowISO();
  const nextPct = clampPct(update.watchedPct);
  const position = Math.max(0, update.positionSeconds || 0);

  const existing = (await db
    .select({
      watched_pct: video_watch_progress.watched_pct,
      completed_at: video_watch_progress.completed_at,
    })
    .from(video_watch_progress)
    .where(eq(video_watch_progress.id, id))
    .limit(1)) as ExistingRow[];

  const prev = existing[0];
  const monotonicPct = prev ? Math.max(prev.watched_pct ?? 0, nextPct) : nextPct;
  // completed_at: kept if it already existed; stamped now if the client marks it.
  const completedAt = prev?.completed_at ?? (update.completed ? now : null);

  if (prev) {
    await db
      .update(video_watch_progress)
      .set({
        position_seconds: position,
        watched_pct: monotonicPct,
        completed_at: completedAt,
        updated_at: now,
      })
      .where(eq(video_watch_progress.id, id));
  } else {
    await db.insert(video_watch_progress).values({
      id,
      user_id: userId,
      video_id: videoId,
      position_seconds: position,
      watched_pct: monotonicPct,
      completed_at: completedAt,
      created_at: now,
      updated_at: now,
    });
  }
  return id;
}

/**
 * Does the progress meet the block's completion threshold? Pure. Feeds the
 * `video` block's grade (mirrors `minWatchPct`, default 0.9).
 */
export function isWatchComplete(watchedPct: number, minWatchPct: number): boolean {
  return clampPct(watchedPct) >= clampPct(minWatchPct);
}
